import autoBind from "auto-bind";
import {proxy} from "valtio";

import {IQueryParamProvider} from "@/shared/contracts";
import {DateHelper} from "@/shared/lib";

import {BaseCalendar, BaseCalendarConfig} from "./base-calendar";

export interface DateRange {
    startDate: Date | null;
    endDate: Date | null;
}

/** Input type that accepts both Date objects and string dates (for server→client serialization) */
export interface DateRangeInput {
    startDate?: Date | string | null;
    endDate?: Date | string | null;
}

export interface DateRangePickerConfig extends BaseCalendarConfig {
    initialValue?: DateRangeInput;
    minDays?: number;
    maxDays?: number;
    /** If true, clicking on the selected start date again will clear the selection */
    clearOnSameStartClick?: boolean;
}

interface DateRangePickerEvents {
    change: (value: DateRange) => void;
    startSelect: (date: Date) => void;
    endSelect: (date: Date) => void;
    clear: () => void;
    monthChange: (month: number, year: number) => void;
}

export interface CalendarDay {
    date: Date;
    day: number;
    weekday: string;
    isCurrentMonth: boolean;
    isToday: boolean;
    isStartDate: boolean;
    isEndDate: boolean;
    isInRange: boolean;
    isInHoverRange: boolean;
    isHoverBefore: boolean; // true when hover date is before start date
    isDisabled: boolean;
    fullDate: string;
}

export class DateRangePickerClass extends BaseCalendar<DateRangePickerEvents> implements IQueryParamProvider {
    // Reactive state
    _startDate: Date | null;
    _endDate: Date | null;
    _hoverDate: Date | null;
    _isSelectingEnd: boolean;
    _secondViewMonth: number;
    _secondViewYear: number;
    _refreshKey: number;

    private readonly minDays?: number;
    private readonly maxDays?: number;
    private readonly clearOnSameStartClick: boolean;

    constructor(config: DateRangePickerConfig = {}) {
        super(config);

        // Parse input dates (accepts both Date objects and strings)
        const inputStart = config.initialValue?.startDate;
        const inputEnd = config.initialValue?.endDate;
        this._startDate = inputStart instanceof Date ? inputStart : DateHelper.parseLocalDate(inputStart);
        this._endDate = inputEnd instanceof Date ? inputEnd : DateHelper.parseLocalDate(inputEnd);

        this._hoverDate = null;
        this._isSelectingEnd = false;
        this._refreshKey = 0;
        this.minDays = config.minDays;
        this.maxDays = config.maxDays;
        this.clearOnSameStartClick = config.clearOnSameStartClick ?? false;

        // Set view to start date or current month
        if (this._startDate) {
            this._viewMonth = this._startDate.getMonth();
            this._viewYear = this._startDate.getFullYear();
        }

        // Second calendar shows next month
        if (this._viewMonth === 11) {
            this._secondViewMonth = 0;
            this._secondViewYear = this._viewYear + 1;
        } else {
            this._secondViewMonth = this._viewMonth + 1;
            this._secondViewYear = this._viewYear;
        }
    }

    // Computed getters
    get startDate(): Date | null {
        return this._startDate;
    }

    get endDate(): Date | null {
        return this._endDate;
    }

    get value(): DateRange {
        return {
            startDate: this._startDate,
            endDate: this._endDate,
        };
    }

    get hasValue(): boolean {
        return this._startDate !== null && this._endDate !== null;
    }

    get hasStartDate(): boolean {
        return this._startDate !== null;
    }

    // Get dates as query string (ready to use in URL)
    // Format: sd=MM-DD-YYYY&ed=MM-DD-YYYY (or custom param names)
    getQueryParam(sd = "sd", ed = "ed"): string {
        const params: Array<string> = [];

        if (this._startDate) {
            const startDate = this.formatDateToMMDDYYYY(this._startDate).replace(/\//g, "-");
            params.push(`${sd}=${startDate}`);
        }

        if (this._endDate) {
            const endDate = this.formatDateToMMDDYYYY(this._endDate).replace(/\//g, "-");
            params.push(`${ed}=${endDate}`);
        }

        return params.join("&");
    }

    /**
     * Get dates as strings in YYYY-MM-DD format
     */
    getDateStrings(): {startDate?: string; endDate?: string} {
        return {
            startDate: this._startDate ? this._startDate.toISOString().split("T")[0] : undefined,
            endDate: this._endDate ? this._endDate.toISOString().split("T")[0] : undefined,
        };
    }

    // Set dates from query params (URLSearchParams or object with custom keys)
    setFromQueryParams(
        params: URLSearchParams | Record<string, string | undefined>,
        paramNames: {start: string; end: string} = {start: "sd", end: "ed"},
        options: {allowPast?: boolean} = {},
    ): void {
        const startParamName = paramNames.start;
        const endParamName = paramNames.end;

        let startDateString: string | null | undefined;
        let endDateString: string | null | undefined;

        if (params instanceof URLSearchParams) {
            startDateString = params.get(startParamName);
            endDateString = params.get(endParamName);
        } else {
            startDateString = params[startParamName];
            endDateString = params[endParamName];
        }

        const startDate = startDateString ? this.parseDate(startDateString) : null;
        const endDate = endDateString ? this.parseDate(endDateString) : null;

        if (startDate || endDate) {
            this.setValue({startDate, endDate}, true, {skipMinCheck: options.allowPast});
        } else {
            this.clear(true);
        }

        // Navigate view to the start date (or min if start is before min and allowPast is false)
        const viewTarget = startDate ?? endDate;
        if (viewTarget) {
            const minDate = this.min;
            const isPast = minDate && viewTarget < minDate;
            const targetYear = isPast && !options.allowPast ? minDate.getFullYear() : viewTarget.getFullYear();
            const targetMonth = isPast && !options.allowPast ? minDate.getMonth() : viewTarget.getMonth();
            this.setViewMonth(targetMonth, targetYear);
        }
    }

    // Set dates from query string
    setFromQueryString(queryString: string, paramNames: {start: string; end: string} = {start: "sd", end: "ed"}): void {
        const params = new URLSearchParams(queryString);
        this.setFromQueryParams(params, paramNames);
    }

    get secondViewMonth(): number {
        return this._secondViewMonth;
    }

    get secondViewYear(): number {
        return this._secondViewYear;
    }

    get secondMonthName(): string {
        return this.formatMonth(new Date(this._secondViewYear, this._secondViewMonth));
    }

    get calendarDays(): Array<CalendarDay> {
        return this.getCalendarDaysForMonth(this._viewMonth, this._viewYear);
    }

    get secondCalendarDays(): Array<CalendarDay> {
        return this.getCalendarDaysForMonth(this._secondViewMonth, this._secondViewYear);
    }

    protected getCalendarDaysForMonth(month: number, year: number): Array<CalendarDay> {
        const daysInMonth = this.getDaysInMonth(month, year);
        const firstDay = daysInMonth[0];
        const previousMonthDays = this.getPreviousMonthDays(firstDay);
        const allDays = [...previousMonthDays, ...daysInMonth];
        const nextMonthDays = this.getNextMonthDays(allDays.length);

        return [
            ...previousMonthDays.map((date) => this.createCalendarDay(date, false, month, year)),
            ...daysInMonth.map((date) => this.createCalendarDay(date, true, month, year)),
            ...nextMonthDays.map((date) => this.createCalendarDay(date, false, month, year)),
        ];
    }

    // Validation helpers
    private isValidRange(startDate: Date, endDate: Date): boolean {
        // End must be after start
        if (!this.isAfter(endDate, startDate) && !this.isSameDay(startDate, endDate)) {
            return false;
        }

        // Check min/max days
        const daysDiff = this.getDaysDifference(startDate, endDate);

        if (this.minDays !== undefined && daysDiff < this.minDays) {
            return false;
        }

        return !(this.maxDays !== undefined && daysDiff > this.maxDays);
    }

    private getDaysDifference(date1: Date, date2: Date): number {
        const timeDiff = Math.abs(date2.getTime() - date1.getTime());
        return Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    }

    private isDateInSelectedRange(date: Date): boolean {
        if (!this._startDate || !this._endDate) return false;

        return (
            (this.isAfter(date, this._startDate) || this.isSameDay(date, this._startDate)) &&
            (this.isBefore(date, this._endDate) || this.isSameDay(date, this._endDate))
        );
    }

    private isDateInHoverRange(date: Date): boolean {
        // Only show hover range when we have start date but no end date
        if (!this._startDate || this._endDate || !this._hoverDate) return false;

        // Don't show hover if hovering on disabled date
        if (!this.isDateInRange(date)) return false;

        const start = this._startDate;
        const end = this._hoverDate;

        // Swap if hover is before start
        const [rangeStart, rangeEnd] = this.isBefore(end, start) ? [end, start] : [start, end];

        return (
            (this.isAfter(date, rangeStart) || this.isSameDay(date, rangeStart)) && (this.isBefore(date, rangeEnd) || this.isSameDay(date, rangeEnd))
        );
    }

    private createCalendarDay(date: Date, isCurrentMonth: boolean, calendarMonth: number, calendarYear: number): CalendarDay {
        // Check if date is disabled
        let isDisabled = !this.isDateInRange(date);

        // If we have start date but no end date, disable dates outside valid range
        if (this._startDate && !this._endDate) {
            if (this.maxDays !== undefined) {
                const daysDiff = this.getDaysDifference(this._startDate, date);
                if (daysDiff > this.maxDays) {
                    isDisabled = true;
                }
            }

            if (this.minDays !== undefined) {
                const daysDiff = this.getDaysDifference(this._startDate, date);
                if (daysDiff < this.minDays && !this.isSameDay(date, this._startDate)) {
                    isDisabled = true;
                }
            }
        }

        // Check if belongs to current calendar month
        const belongsToCalendarMonth = isCurrentMonth && date.getMonth() === calendarMonth;

        // Only show selected dates and ranges for dates that belong to the calendar month
        const isStartDate = belongsToCalendarMonth && this._startDate ? this.isSameDay(date, this._startDate) : false;
        const isEndDate = belongsToCalendarMonth && this._endDate ? this.isSameDay(date, this._endDate) : false;
        const isInRange = belongsToCalendarMonth && this.isDateInSelectedRange(date);
        const isInHoverRange = belongsToCalendarMonth && this.isDateInHoverRange(date);

        // Check if hover is before start (for direction indicator)
        const isHoverBefore = this._startDate && this._hoverDate && !this._endDate ? this.isBefore(this._hoverDate, this._startDate) : false;

        return {
            date,
            day: date.getDate(),
            weekday: this.formatWeekday(date),
            isCurrentMonth: belongsToCalendarMonth,
            isToday: this.isToday(date, calendarMonth, calendarYear),
            isStartDate,
            isEndDate,
            isInRange,
            isInHoverRange,
            isHoverBefore,
            isDisabled,
            fullDate: this.formatDate(date),
        };
    }

    // Actions
    setValue(value: DateRange, silent = false, options: {skipMinCheck?: boolean} = {}): void {
        if (!options.skipMinCheck && value.startDate && !this.isDateInRange(value.startDate)) {
            console.warn("DateRangePicker: Start date is out of range", value.startDate);
            return;
        }

        if (!options.skipMinCheck && value.endDate && !this.isDateInRange(value.endDate)) {
            console.warn("DateRangePicker: End date is out of range", value.endDate);
            return;
        }

        if (value.startDate && value.endDate && !this.isValidRange(value.startDate, value.endDate)) {
            console.warn("DateRangePicker: Invalid range", value);
            return;
        }

        this._startDate = value.startDate;
        this._endDate = value.endDate;
        this._isSelectingEnd = false;

        // Keep the view in sync with the selected start date
        if (value.startDate) {
            this.setViewMonth(value.startDate.getMonth(), value.startDate.getFullYear());
        }

        if (!silent) {
            this.events.emit("change", this.value);
        }
    }

    sync(value: DateRange): void {
        this.setValue({
            startDate: value.startDate ?? null,
            endDate: value.endDate ?? null,
        });
    }

    selectDay(day: CalendarDay): void {
        if (day.isDisabled || this._disabled) return;

        // If no start date or we're resetting, set start date
        if (!this._startDate || (this._startDate && this._endDate)) {
            this._startDate = day.date;
            this._endDate = null;
            this._isSelectingEnd = true;
            this.events.emit("startSelect", day.date);
        }
        // If we have start date but no end date
        else if (this._startDate && !this._endDate) {
            // If clicking on the same start date, clear selection (if option enabled)
            if (this.clearOnSameStartClick && this.isSameDay(day.date, this._startDate)) {
                this.clear();
                return;
            }

            // If selected date is before start, swap them
            if (this.isBefore(day.date, this._startDate)) {
                this._endDate = this._startDate;
                this._startDate = day.date;
            } else {
                this._endDate = day.date;
            }

            // Validate range
            if (!this.isValidRange(this._startDate, this._endDate)) {
                console.warn("DateRangePicker: Selected range is invalid");
                this._endDate = null;
                return;
            }
            this._isSelectingEnd = false;
            this.events.emit("endSelect", day.date);
        }

        this.events.emit("change", this.value);
    }

    // Convenience methods for testing/direct usage
    selectStartDate(date: Date): void {
        if (!this.isDateInRange(date)) {
            console.warn("DateRangePicker: Start date is out of range", date);
            return;
        }

        this._startDate = date;
        this._endDate = null;
        this._isSelectingEnd = true;
        this.events.emit("startSelect", date);
        this.events.emit("change", this.value);
    }

    selectEndDate(date: Date): void {
        if (!this._startDate) {
            console.warn("DateRangePicker: Cannot select end date without start date");
            return;
        }

        if (!this.isDateInRange(date)) {
            console.warn("DateRangePicker: End date is out of range", date);
            return;
        }

        // If selected date is before start, swap them
        let startDate = this._startDate;
        let endDate = date;

        if (this.isBefore(date, this._startDate)) {
            startDate = date;
            endDate = this._startDate;
        }

        // Validate range
        if (!this.isValidRange(startDate, endDate)) {
            console.warn("DateRangePicker: Selected range is invalid");
            return;
        }

        this._startDate = startDate;
        this._endDate = endDate;
        this._isSelectingEnd = false;
        this.events.emit("endSelect", date);
        this.events.emit("change", this.value);
    }

    clear(silent = false): void {
        this._startDate = null;
        this._endDate = null;
        this._hoverDate = null;
        this._isSelectingEnd = false;
        if (!silent) {
            this.events.emit("change", this.value);
            this.events.emit("clear");
        }
    }

    setHoverDate(date: Date | null): void {
        this._hoverDate = date;
    }

    clearHoverDate(): void {
        this._hoverDate = null;
    }

    /**
     * Force calendar to re-render and recalculate disabled dates
     */
    forceUpdate(): void {
        this._refreshKey++;
    }

    reset(): void {
        this._startDate = null;
        this._endDate = null;
        this._isSelectingEnd = false;

        const now = new Date();
        this._viewMonth = now.getMonth();
        this._viewYear = now.getFullYear();

        this.events.emit("change", this.value);
    }

    // Navigation (override to sync both calendars)
    nextMonth(): void {
        super.nextMonth();
        this.updateSecondCalendar();
        this.events.emit("monthChange", this._viewMonth, this._viewYear);
    }

    previousMonth(): void {
        super.previousMonth();
        this.updateSecondCalendar();
        this.events.emit("monthChange", this._viewMonth, this._viewYear);
    }

    nextYear(): void {
        super.nextYear();
        this.updateSecondCalendar();
        this.events.emit("monthChange", this._viewMonth, this._viewYear);
    }

    previousYear(): void {
        super.previousYear();
        this.updateSecondCalendar();
        this.events.emit("monthChange", this._viewMonth, this._viewYear);
    }

    goToToday(): void {
        super.goToToday();
        this.updateSecondCalendar();
        this.events.emit("monthChange", this._viewMonth, this._viewYear);
    }

    setViewMonth(month: number, year: number): void {
        super.setViewMonth(month, year);
        this.updateSecondCalendar();
        this.events.emit("monthChange", this._viewMonth, this._viewYear);
    }

    private updateSecondCalendar(): void {
        // Second calendar always shows next month
        if (this._viewMonth === 11) {
            this._secondViewMonth = 0;
            this._secondViewYear = this._viewYear + 1;
        } else {
            this._secondViewMonth = this._viewMonth + 1;
            this._secondViewYear = this._viewYear;
        }
    }
}

// Export type
export type DateRangePickerInstance = DateRangePickerClass;

// Factory function (SSR compatible)
export function DateRangePicker(config?: DateRangePickerConfig): DateRangePickerInstance {
    return autoBind(proxy<DateRangePickerInstance>(new DateRangePickerClass(config)));
}
